/**
 * API client for team endpoints.
 *
 * A Team is a facilitator-led cohort layered on top of a paid Group.
 * The facilitator picks one active story at a time; eligible Group
 * members join via an invite code and play together. Analyses can be
 * viewed individually and aggregated at the team-story level.
 *
 * Mirrors the envelope-unwrap pattern used by analysisService and
 * playerSessionService: `response.data.data || response.data`.
 * @module teamService
 */

import apiClient from '../lib/apiClient'

export const teamService = {
  async listTeams() {
    const response = await apiClient.get('/api/teams')
    return response.data.data || response.data
  },

  async getTeam(teamId) {
    const response = await apiClient.get(`/api/teams/${teamId}`)
    return response.data.data || response.data
  },

  async getTeamByCode(code) {
    const response = await apiClient.get(`/api/teams/by_code/${code}`)
    return response.data.data || response.data
  },

  /**
   * `groupId` is optional — when omitted the backend derives the team's
   * Group from the facilitator's enrollment for `firstStoryId`. Pass it
   * explicitly only if the same story is reachable through multiple Groups
   * and the facilitator needs to disambiguate.
   */
  async createTeam({ name, groupId, firstStoryId, facilitatorPlays }) {
    const payload = {
      name,
      first_story_id: firstStoryId,
      facilitator_plays: facilitatorPlays,
    }
    if (groupId != null) payload.group_id = groupId
    const response = await apiClient.post('/api/teams', payload)
    return response.data.data || response.data
  },

  async joinTeam(teamId, displayName) {
    const response = await apiClient.post(`/api/teams/${teamId}/join`, {
      display_name: displayName,
    })
    return response.data.data || response.data
  },

  /**
   * Facilitator force-ends an active team story without adding a follow-up.
   * Same status flip the server applies automatically when the last active
   * member finishes their session — used when stragglers have abandoned
   * and the facilitator wants the Team Analysis surfaced now.
   */
  async endTeamStory(teamId, teamStoryId) {
    const response = await apiClient.post(`/api/teams/${teamId}/stories/${teamStoryId}/end`)
    return response.data.data || response.data
  },

  /**
   * Facilitator advances the team to a new story.
   * Implicitly completes the prior active story. Response includes
   * `members_lacking_access` so the UI can nudge the facilitator to
   * enroll those members in the underlying Group.
   */
  async addStory(teamId, storyId) {
    const response = await apiClient.post(`/api/teams/${teamId}/stories`, {
      story_id: storyId,
    })
    return response.data.data || response.data
  },

  /**
   * Fetch the team analysis for a completed team-story.
   * Returns `{ status: 'processing' }` (202) while aggregation is running.
   * Backend scopes the response shape: facilitators see full member info,
   * members see anonymized aggregate + their own data.
   */
  async getTeamAnalysis(teamId, teamStoryId) {
    const response = await apiClient.get(
      `/api/teams/${teamId}/stories/${teamStoryId}/analysis`,
    )
    if (response.status === 202) {
      return response.data
    }
    return response.data.data || response.data
  },

  // ---- Facilitator/admin team management -----------------------------------

  /**
   * Create N teams under a Group in one call. Any slot without an explicit
   * name gets an auto-generated thematic name. `distribute: true` spreads the
   * Group roster round-robin across the new teams.
   */
  async batchCreateTeams({ groupId, firstStoryId, count, names, distribute }) {
    const payload = {
      first_story_id: firstStoryId,
      count,
      names,
      distribute,
    }
    // groupId is optional — the backend derives the Group from the story when
    // omitted (the worlds facilitator flow only knows the story).
    if (groupId != null) payload.group_id = groupId
    const response = await apiClient.post('/api/teams/batch', payload)
    return response.data.data || response.data
  },

  async renameTeam(teamId, name) {
    const response = await apiClient.patch(`/api/teams/${teamId}`, { name })
    return response.data.data || response.data
  },

  async disbandTeam(teamId) {
    await apiClient.delete(`/api/teams/${teamId}`)
  },

  async addMember(teamId, userId) {
    const response = await apiClient.post(`/api/teams/${teamId}/members`, {
      user_id: userId,
    })
    return response.data.data || response.data
  },

  async removeMember(teamId, memberId) {
    const response = await apiClient.delete(`/api/teams/${teamId}/members/${memberId}`)
    return response.data.data || response.data
  },

  /** Move a member to a sibling team in the same Group. */
  async reassignMember(teamId, memberId, toTeamId) {
    const response = await apiClient.patch(`/api/teams/${teamId}/members/${memberId}`, {
      to_team_id: toTeamId,
    })
    return response.data.data || response.data
  },

  /** Facilitator override: free a role a member already locked. */
  async resetMemberRole(teamId, memberId) {
    const response = await apiClient.patch(
      `/api/teams/${teamId}/members/${memberId}/reset_role`,
    )
    return response.data.data || response.data
  },

  /**
   * Set per-role claim caps for the team's active story.
   * `roleCaps` is a map of roleKey -> integer (1 = unique default, >1 = up to N,
   * 0 or negative = unlimited).
   */
  async setRoleCaps(teamId, teamStoryId, roleCaps) {
    const response = await apiClient.patch(
      `/api/teams/${teamId}/stories/${teamStoryId}/role_caps`,
      { role_caps: roleCaps },
    )
    return response.data.data || response.data
  },
}
